import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, UserMinus, Repeat, X } from "lucide-react";
import { authFetch } from "@/lib/authFetch";
import { startLinkJob } from "@/lib/linkJobs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { HistoryTimeline } from "@/components/HistoryTimeline";

const LIMIT = 200;
const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
export const LINK_ROLES = { assigned_user: "Assigned User", lender: "Affiliated Lender", affiliate_1: "Affiliate 1", affiliate_2: "Affiliate 2" };
const ROLE_STYLE = { assigned_user: "bg-violet-100 text-violet-800", lender: "bg-emerald-100 text-emerald-800", affiliate_1: "bg-sky-100 text-sky-800", affiliate_2: "bg-amber-100 text-amber-800" };
const EFFECT = {
  assigned_user: "They lose access to this client in the portal. Their login is not changed.",
  lender: "The Affiliated Lender field is updated in the portal and the CRM.",
  affiliate_1: "The Affiliate 1 field is updated in the portal and the CRM.",
  affiliate_2: "The Affiliate 2 field is updated in the portal and the CRM.",
};

export const RoleTag = ({ role }) => <Badge className={ROLE_STYLE[role]} data-testid={`link-tag-${role}`}>{LINK_ROLES[role]}</Badge>;

const candidateCache = {};
export const loadCandidates = (role) => {
  candidateCache[role] = candidateCache[role] || authFetch(`${API}/link-candidates/${role}`, { credentials: "include" })
    .then((r) => r.json()).then((d) => d.candidates || []).catch(() => { delete candidateCache[role]; return []; });
  return candidateCache[role];
};
const useCandidates = (role) => {
  const [list, setList] = useState(null);
  useEffect(() => { if (role) loadCandidates(role).then(setList); }, [role]);
  return list;
};

export const CandidatePicker = ({ role, exclude, value, onChange, testId = "link-candidate" }) => {
  const list = useCandidates(role) || [];
  const [q, setQ] = useState("");
  const matches = list.filter((c) => c.user_id !== exclude && `${c.name} ${c.email} ${c.company}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-2">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or company" data-testid={`${testId}-search`} />
      <div className="max-h-44 overflow-y-auto rounded-md border divide-y" data-testid={`${testId}-list`}>
        {matches.length === 0 && <p className="p-3 text-sm text-slate-500">No matching people</p>}
        {matches.slice(0, 50).map((c) => (
          <button key={c.user_id} type="button" onClick={() => onChange(c)} data-testid={`${testId}-${c.user_id}`}
            className={`w-full text-left px-3 py-2 text-sm transition-colors ${value?.user_id === c.user_id ? "bg-[#357c2a] text-white" : "hover:bg-slate-100"}`}>
            <span className="font-medium">{c.name}</span> <span className="opacity-70">{c.email}</span>
          </button>
        ))}
      </div>
      {matches.length > 50 && <p className="text-xs text-slate-500">Showing 50 of {matches.length} — type to search</p>}
    </div>
  );
};

const ActionDialog = ({ person, action, onClose, onDone }) => {
  const [role, setRole] = useState("");
  const [target, setTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setRole(action?.client.reasons.length === 1 ? action.client.reasons[0] : ""); setTarget(null); }, [action]);
  if (!action) return null;
  const { client, type } = action;
  const reassign = type === "reassign";

  const submit = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/linked-clients/${person.user_id}/change`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_id: client.client_id, role, action: type, new_user_id: target?.user_id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Not saved");
      toast.success(data.message);
      if (data.still_linked_as?.length) toast.info(`${person.name} is still linked to ${client.name} as ${data.still_linked_as.join(", ")}`);
      onDone();
    } catch (e) {
      toast.error(e.message, { duration: 10000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="link-action-dialog">
        <DialogHeader>
          <DialogTitle>{reassign ? "Reassign" : "Remove link"}: {client.name}</DialogTitle>
          <DialogDescription>{reassign ? `Replace ${person.name} with someone else in the same role.` : `Remove ${person.name} from this client.`}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Role</Label>
            <Select value={role} onValueChange={(v) => { setRole(v); setTarget(null); }}>
              <SelectTrigger className="mt-1" data-testid="link-action-role-select"><SelectValue placeholder="Pick the role" /></SelectTrigger>
              <SelectContent>{client.reasons.map((r) => <SelectItem key={r} value={r}>{LINK_ROLES[r]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {reassign && role && (
            <div>
              <Label>New {LINK_ROLES[role]}</Label>
              <CandidatePicker key={role} role={role} exclude={person.user_id} value={target} onChange={setTarget} />
            </div>
          )}
          {role && (!reassign || target) && (
            <p className="text-sm rounded-md bg-amber-50 text-amber-900 p-3" data-testid="link-action-summary">
              {reassign ? <>Replace <b>{person.name}</b> with <b>{target.name}</b> as <b>{LINK_ROLES[role]}</b> on <b>{client.name}</b>.</>
                : <>Remove <b>{person.name}</b> as <b>{LINK_ROLES[role]}</b> on <b>{client.name}</b>.</>}{" "}{EFFECT[role]}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="link-action-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving || !role || (reassign && !target)} variant={reassign ? "default" : "destructive"} data-testid="link-action-confirm">
            {saving ? "Saving..." : reassign ? "Confirm reassign" : "Confirm remove"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Bulk: one pick per role (or leave alone), optional "use for all roles", then a confirmation step.
const BulkDialog = ({ person, rows, mode, onClose, onStarted }) => {
  const reassign = mode === "reassign";
  const counts = useMemo(() => {
    const c = {};
    rows.forEach((r) => r.reasons.forEach((x) => { c[x] = (c[x] || 0) + 1; }));
    return c;
  }, [rows]);
  const roles = Object.keys(LINK_ROLES).filter((r) => counts[r]);
  const [picks, setPicks] = useState({});
  const [removeRoles, setRemoveRoles] = useState(() => Object.fromEntries(roles.map((r) => [r, true])));
  const [editing, setEditing] = useState(roles[0]);
  const [step, setStep] = useState("pick");
  const [busy, setBusy] = useState(false);
  const [lists, setLists] = useState({});
  useEffect(() => { roles.forEach((r) => loadCandidates(r).then((l) => setLists((s) => ({ ...s, [r]: l })))); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const chosen = reassign ? roles.filter((r) => picks[r]) : roles.filter((r) => removeRoles[r]);
  const first = reassign ? picks[chosen[0]] : null;
  const canUseForAll = first && roles.some((r) => !picks[r] && (lists[r] || []).some((c) => c.user_id === first.user_id));
  const useForAll = () => setPicks((p) => {
    const next = { ...p };
    roles.forEach((r) => { if ((lists[r] || []).some((c) => c.user_id === first.user_id)) next[r] = first; });
    return next;
  });

  const go = async () => {
    setBusy(true);
    const items = [];
    rows.forEach((row) => row.reasons.forEach((r) => {
      if (chosen.includes(r)) items.push({ client_id: row.client_id, role: r, from_user_id: person.user_id, new_user_id: reassign ? picks[r].user_id : null });
    }));
    try {
      await startLinkJob(items, "bulk", `${reassign ? "Reassigning" : "Removing"} ${person.name} on ${rows.length} client${rows.length === 1 ? "" : "s"}`);
      onStarted();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bulk-link-dialog">
        <DialogHeader>
          <DialogTitle>{reassign ? "Reassign" : "Remove"} {person.name} on {rows.length} client{rows.length === 1 ? "" : "s"}</DialogTitle>
          <DialogDescription>Each role moves within that role. Saves to the CRM first; rejected clients stay as they are. Owner and Betenbough location are never changed.</DialogDescription>
        </DialogHeader>
        {step === "pick" ? (
          <div className="space-y-3">
            {roles.map((r) => (
              <div key={r} className="rounded-lg border p-3 space-y-2" data-testid={`bulk-role-${r}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2"><RoleTag role={r} /><span className="text-sm text-slate-600">{counts[r]} client{counts[r] === 1 ? "" : "s"}</span></div>
                  {reassign ? (
                    picks[r] ? (
                      <span className="text-sm flex items-center gap-1" data-testid={`bulk-pick-${r}`}>→ <b>{picks[r].name}</b>
                        <button onClick={() => setPicks((p) => ({ ...p, [r]: null }))} aria-label="Leave alone" data-testid={`bulk-clear-${r}`}><X className="h-3.5 w-3.5 text-slate-400" /></button>
                      </span>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setEditing(r)} data-testid={`bulk-choose-${r}`}>{editing === r ? "Choosing…" : "Choose person"}</Button>
                    )
                  ) : (
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox checked={!!removeRoles[r]} onCheckedChange={(v) => setRemoveRoles((s) => ({ ...s, [r]: !!v }))} data-testid={`bulk-remove-${r}`} />Remove
                    </label>
                  )}
                </div>
                {reassign && editing === r && !picks[r] && (
                  <CandidatePicker role={r} exclude={person.user_id} value={null} testId={`bulk-candidate-${r}`}
                    onChange={(c) => { setPicks((p) => ({ ...p, [r]: c })); setEditing(roles.find((x) => x !== r && !picks[x])); }} />
                )}
                {reassign && !picks[r] && editing !== r && <p className="text-xs text-slate-500">Left alone</p>}
              </div>
            ))}
            {canUseForAll && <Button variant="secondary" size="sm" onClick={useForAll} data-testid="bulk-use-for-all">Use {first.name} for all roles they can hold</Button>}
          </div>
        ) : (
          <div className="rounded-md bg-amber-50 text-amber-900 p-3 text-sm space-y-1" data-testid="bulk-confirm-summary">
            <p>This will change:</p>
            <ul className="list-disc pl-5">
              {chosen.map((r) => <li key={r}><b>{LINK_ROLES[r]}</b>: {counts[r]} client{counts[r] === 1 ? "" : "s"} → {reassign ? <b>{picks[r].name}</b> : "removed"}</li>)}
            </ul>
            {roles.filter((r) => !chosen.includes(r)).length > 0 && <p>Left alone: {roles.filter((r) => !chosen.includes(r)).map((r) => LINK_ROLES[r]).join(", ")}.</p>}
            {reassign && <p>Clients where the new person is already set are skipped.</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={step === "pick" ? onClose : () => setStep("pick")} data-testid="bulk-back-btn">{step === "pick" ? "Cancel" : "Back"}</Button>
          {step === "pick"
            ? <Button onClick={() => setStep("confirm")} disabled={!chosen.length} data-testid="bulk-review-btn">Review</Button>
            : <Button onClick={go} disabled={busy} variant={reassign ? "default" : "destructive"} data-testid="bulk-confirm-btn">{busy ? "Starting…" : reassign ? "Confirm reassign" : "Confirm remove"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const LinkedRow = ({ c, checked, onCheck, onAction }) => (
  <TableRow data-testid={`linked-client-row-${c.client_id}`}>
    <TableCell><Checkbox checked={checked} onCheckedChange={(v) => onCheck(c.client_id, !!v)} aria-label={`Select ${c.name}`} data-testid={`linked-client-check-${c.client_id}`} /></TableCell>
    <TableCell>
      <Link to={`/admin/clients/${c.client_id}`} className="font-medium text-[#357c2a] hover:underline" data-testid={`linked-client-open-${c.client_id}`}>{c.name}</Link>
    </TableCell>
    <TableCell className="text-sm">{c.location || "—"}</TableCell>
    <TableCell className="text-sm">{c.status || "—"}</TableCell>
    <TableCell className="text-sm">{c.counselor || "—"}</TableCell>
    <TableCell><div className="flex flex-wrap gap-1">{c.reasons.map((r) => <RoleTag key={r} role={r} />)}</div></TableCell>
    <TableCell className="text-right whitespace-nowrap">
      <Button size="sm" variant="ghost" title="Reassign" aria-label={`Reassign ${c.name}`} onClick={() => onAction({ client: c, type: "reassign" })} data-testid={`linked-client-reassign-${c.client_id}`}><Repeat className="h-4 w-4" /></Button>
      <Button size="sm" variant="ghost" title="Remove link" aria-label={`Remove link to ${c.name}`} className="text-red-600" onClick={() => onAction({ client: c, type: "remove" })} data-testid={`linked-client-remove-${c.client_id}`}><UserMinus className="h-4 w-4" /></Button>
    </TableCell>
  </TableRow>
);

const BulkBar = ({ n, total, onAll, onClear, onReassign, onRemove }) => (
  <div className="flex flex-wrap items-center gap-2 rounded-lg bg-[#357c2a]/10 border border-[#357c2a]/30 px-3 py-2 text-sm" data-testid="linked-bulk-bar">
    <b data-testid="linked-selected-count">{n} selected</b>
    {n < total && <button className="underline" onClick={onAll} data-testid="linked-select-all-btn">Select all {total}</button>}
    <button className="underline text-slate-600" onClick={onClear} data-testid="linked-clear-selection">Clear</button>
    <div className="ml-auto flex gap-2">
      <Button size="sm" onClick={onReassign} data-testid="linked-bulk-reassign"><Repeat className="h-4 w-4 mr-1" />Reassign selected</Button>
      <Button size="sm" variant="destructive" onClick={onRemove} data-testid="linked-bulk-remove"><UserMinus className="h-4 w-4 mr-1" />Remove selected</Button>
    </div>
  </div>
);

export const LinkedClientsDialog = ({ person, onClose }) => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [action, setAction] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulk, setBulk] = useState(null);
  const [histKey, setHistKey] = useState(0);

  const load = async () => {
    const res = await authFetch(`${API}/linked-clients/${person.user_id}`, { credentials: "include" });
    if (res.ok) setData(await res.json());
    else toast.error("Could not load linked clients");
  };
  useEffect(() => { if (person) { setData(null); setQ(""); setRole("all"); setSelected(new Set()); load(); } }, [person?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const h = () => { if (person) { load(); setHistKey((k) => k + 1); } };
    window.addEventListener("link-job-done", h);
    return () => window.removeEventListener("link-job-done", h);
  }, [person?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = useMemo(() => (data?.clients || []).filter((c) => (role === "all" || c.reasons.includes(role)) && c.name.toLowerCase().includes(q.toLowerCase())), [data, q, role]);
  if (!person) return null;
  const all = data?.clients || [];
  const check = (id, v) => setSelected((s) => { const n = new Set(s); v ? n.add(id) : n.delete(id); return n; });
  const pageChecked = shown.length > 0 && shown.slice(0, LIMIT).every((c) => selected.has(c.client_id));
  const togglePage = (v) => setSelected((s) => { const n = new Set(s); shown.slice(0, LIMIT).forEach((c) => (v ? n.add(c.client_id) : n.delete(c.client_id))); return n; });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="linked-clients-dialog">
        <DialogHeader>
          <DialogTitle>Linked Clients — {person.name}</DialogTitle>
          <DialogDescription data-testid="linked-clients-count">
            {data ? `${data.total} linked client${data.total === 1 ? "" : "s"}` : "Loading…"} · Assigned User, Affiliated Lender, Affiliate 1 or Affiliate 2
          </DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="clients">
          <TabsList>
            <TabsTrigger value="clients" data-testid="linked-tab-clients">Clients</TabsTrigger>
            <TabsTrigger value="history" data-testid="linked-tab-history">History</TabsTrigger>
          </TabsList>
          <TabsContent value="clients" className="space-y-3">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative w-full max-w-xs">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client name" className="pl-9" data-testid="linked-clients-search" />
              </div>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="w-48" data-testid="linked-clients-role-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All reasons</SelectItem>
                  {Object.entries(LINK_ROLES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {selected.size > 0 && (
              <BulkBar n={selected.size} total={all.length} onAll={() => setSelected(new Set(all.map((c) => c.client_id)))} onClear={() => setSelected(new Set())}
                onReassign={() => setBulk("reassign")} onRemove={() => setBulk("remove")} />
            )}
            <div className="rounded-lg border">
              <Table data-testid="linked-clients-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"><Checkbox checked={pageChecked} onCheckedChange={(v) => togglePage(!!v)} aria-label="Select listed" data-testid="linked-select-page" /></TableHead>
                    <TableHead>Client</TableHead><TableHead>Location</TableHead><TableHead>Status</TableHead><TableHead>Counselor</TableHead><TableHead>Why linked</TableHead><TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!data ? <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-500">Loading…</TableCell></TableRow>
                    : shown.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-500" data-testid="linked-clients-empty">{q || role !== "all" ? "No clients match this filter" : "No linked clients"}</TableCell></TableRow>
                    : shown.slice(0, LIMIT).map((c) => <LinkedRow key={c.client_id} c={c} checked={selected.has(c.client_id)} onCheck={check} onAction={setAction} />)}
                </TableBody>
              </Table>
            </div>
            {data && <p className="text-xs text-slate-500" data-testid="linked-clients-shown">{shown.length} of {data.total} shown{shown.length > LIMIT ? ` · first ${LIMIT} listed — search or filter to narrow` : ""}</p>}
          </TabsContent>
          <TabsContent value="history">
            <HistoryTimeline params={{ person_id: person.user_id }} reloadKey={histKey} />
          </TabsContent>
        </Tabs>
        <ActionDialog person={person} action={action} onClose={() => setAction(null)} onDone={() => { setAction(null); load(); setHistKey((k) => k + 1); }} />
        {bulk && (
          <BulkDialog person={person} mode={bulk} rows={all.filter((c) => selected.has(c.client_id))} onClose={() => setBulk(null)}
            onStarted={() => { setBulk(null); setSelected(new Set()); }} />
        )}
      </DialogContent>
    </Dialog>
  );
};
